/**
 * Runtime configuration (web-client.md §4). Server URL precedence:
 *   1. persisted setting (localStorage, last used)
 *   2. build-time env (VITE_SERVER_URL)
 *   3. default http://localhost:5001 (the S0 dev instance A, backend/run/dev-run.sh)
 */
import type { SettingsStore } from '../core/index.ts';

export const DEFAULT_SERVER_URL = 'http://localhost:5001';

// clientVersion is stamped into sys.hello and X-Client-Version; keep it in step with package.json.
export const CLIENT_VERSION = '0.2.0';

/**
 * Which entry of the server's version manifest describes us. Named once and used by both the update
 * decision and the version readout, because two places spelling this key differently is a client that
 * silently never sees an update.
 */
export const CLIENT_TYPE = 'web';

export interface AppConfig {
  serverUrl: string;
  wsUrl: string;
  clientVersion: string;
  language: string;
  platform: string;
}

/** Derive the /ws endpoint from an http(s) base URL. */
export function toWsUrl(serverUrl: string): string {
  const url = new URL(serverUrl);
  url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
  url.pathname = url.pathname.replace(/\/$/, '') + '/ws';
  return url.toString();
}

function envServerUrl(): string | undefined {
  // import.meta.env is Vite-injected; guard so core stays testable without Vite.
  const env = (import.meta as unknown as { env?: Record<string, string | undefined> }).env;
  return env?.VITE_SERVER_URL;
}

export function resolveConfig(settings: SettingsStore, browserLanguage: string): AppConfig {
  const serverUrl = settings.getServerUrl(envServerUrl() ?? DEFAULT_SERVER_URL);
  const language = settings.getLanguage(browserLanguage.startsWith('ru') ? 'ru' : 'en');
  return {
    serverUrl,
    wsUrl: toWsUrl(serverUrl),
    clientVersion: CLIENT_VERSION,
    language,
    platform: 'web',
  };
}
